/**
 * Второй одностраничный экран оплаты. Его видит половина клиентов тех мерчантов, у которых нет
 * персонального шаблона; вторая половина остаётся на давнем одностраничном экране, и по этой паре
 * сравниваются исходы (см. `src/templates/templateNames.ts`).
 *
 * Попапа «мало времени» здесь нет намеренно: окно уже продлено к моменту показа экрана, поэтому
 * попап обещал бы время, которого не существует, а второго продления не бывает.
 *
 * Как она устроена:
 *  - реквизиты и загрузка чека живут на одном экране, шага в прогрессе два, а не три;
 *  - «Я оплатил» — финальное действие: отправляет чек и подтверждает оплату;
 *  - при первом показе экрана оплаты страница отмечает «клиент платит»
 *    (`POST /api/receipt-intent` через `setShowConfirm`), что даёт тикету 20 минут вместо 10.
 *    Отметку шлём только там, где окно действительно короткое — то есть когда требуется
 *    чек (матчинг); остальным она ничего не меняет, а экран таймаута сдвинула бы.
 *
 * Раннего `confirm-transaction` здесь намеренно нет: он переводит тикет в `FILES_VERIFICATION`
 * (переход `T_CONFIRMED_BY_CLIENT`), то есть отправил бы его оператору без чека и увёл клиента
 * с экрана оплаты. Окно продлевает отметка перехода к чеку, а не подтверждение оплаты.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateProps } from '../types';
import type { ReceiptUploadHandlers } from '../../services/receiptUpload';
import { supportTarget } from '../../services/supportUrl';
import GatePaymentPage from './pages/GatePaymentPage';
import GateUploadPage from './pages/GateUploadPage';
import GateSuccessPage from './pages/GateSuccessPage';
import GateRetryPage from './pages/GateRetryPage';
import {
  GateProcessingScreen,
  GatePaymentProcessingScreen,
  GateReceiptUnconfirmedScreen,
  GatePaymentExpiredScreen,
  GateErrorScreen,
  GateExpiredScreen,
  GateServerErrorScreen,
  GateWaitingScreen,
} from './pages/GateProcessingPage';
import GateVariantPage from './pages/GateVariantPage';
import { REQUISITE_STATUSES, resolveGateStage } from './gateStage';

// Methods we serve — must NOT be bounced to the merchant before the search resolves. Whether the
// client pays on our page (card/SBP requisite) or gets redirected to the provider (redirect_url +
// paymentUrl) is decided by the requisite data at PAYMENT_PENDING, not by this list (CPAYMENT-1661).
const SUPPORTED_METHODS = [
  'SBP P2P RUB',
  'SBP MATCHING RUB',
  'TRANS SBP P2P RUB',
  'OZON SBP RUB',
  'ALFA SBP RUB',
  'SBER SBP RUB',
  'VTB SBP RUB',
  'TBANK SBP RUB',
  'MC/VISA/MIR P2P RUB',
  'P2P MATCHING RUB',
  'MC/VISA P2P KZT',
  'MOBILE P2P KZT',
  'MC/VISA P2P KRW',
  'MC/VISA UAH',
  'PRIVAT24 P2P UAH',
  'M10AZN',
  'P2P QUASI ECOM RUB',
  'MATCHING QUASI ECOM RUB',
  'SBER TRANS P2P RUB',
  'TBANK TRANS P2P RUB',
  'TBANK TRANS SBP P2P RUB',
  'VTB TRANS P2P RUB',
  'YOOMONEY TRANS P2P RUB',
  'SBER TRANS QR P2P RUB',
];

// Redirect methods: the client pays on an external partner page (redirect_url requisite), and the
// deposit may first show the amount tile (WAITING_FOR_VARIANT_SELECTION). They must NOT be bounced
// back to the merchant at CLIENT_PENDING before the search resolves — the tile/requisite would be
// skipped. Payment step still goes to the provider via the redirect_url branch (CPAYMENT-1619).
const REDIRECT_METHODS = ['P2P QUASI ECOM RUB', 'MATCHING QUASI ECOM RUB'];

const PayPageGateV1 = ({
  transaction,
  loading,
  error,
  timeLeft,
  timeoutScreen,
  uuid,
  receiptIntent,
  onConfirm,
  onConfirmWithReceipt,
  onCancel,
  onDeclineOffer,
  cancelReasons,
  onLeave,
  onRedirect,
  onSelectVariant,
  variantSubmitting,
  setRetry,
  setShowConfirm,
  formatTime,
}: TemplateProps) => {
  const { t } = useTranslation();
  const [receiptAccepted, setReceiptAccepted] = useState(false);
  const [processingAfterUpload, setProcessingAfterUpload] = useState(false);
  const [serverError, setServerError] = useState(false);
  // CPAYMENT-1616: bridge the ~3s until the poll clears a stale "failed" after a re-upload.
  const [reuploading, setReuploading] = useState(false);
  // Отметка «клиент платит» уходит один раз за сессию страницы — повторные рендеры её не дублируют.
  const earlyIntentSentRef = useRef(false);

  const formattedTime = formatTime(timeLeft ?? 600);

  const isSupportedMethod = SUPPORTED_METHODS.includes(transaction?.paymentMethod ?? '');
  const isRedirectMethod = REDIRECT_METHODS.includes(transaction?.paymentMethod ?? '');

  const shouldRedirectNoRequisite =
    !!transaction &&
    transaction.status === 'CLIENT_PENDING' &&
    !transaction.requisite?.type &&
    !isSupportedMethod &&
    !isRedirectMethod &&
    (transaction as any).paymentAdapterSearchAttemptsCounter === 0;

  useEffect(() => {
    if (shouldRedirectNoRequisite && transaction?.redirectUrl) {
      onRedirect(transaction.redirectUrl);
    }
  }, [shouldRedirectNoRequisite, transaction?.redirectUrl, onRedirect]);

  /**
   * Окно оплаты — с самого показа реквизитов. Клиент этого экрана уже стоит перед формой загрузки
   * чека, поэтому отметку «пошёл за чеком» ставим сразу, а не по клику: короткие 10 минут гасили бы
   * тикет, пока клиент ищет чек в приложении банка (механика CPAYMENT-1716). Длительность выбирает
   * бэкенд по флагу `onePage` — здесь это 20 минут (CPAYMENT-1753), своим таймером их не считаем.
   *
   * `setShowConfirm` — единственная точка входа на экран чека во всех шаблонах, отметку шлёт она.
   *
   * Редиректные методы (quasi-ecom) сюда не попадают: чек грузят не у нас, клиент вообще не
   * задерживается на этом экране — он уходит на чекаут провайдера ещё до того, как здесь успевает
   * что-то отрисоваться. Отметка для них не только бессмысленна (`external_user_receipt_intent_date`
   * без реального намерения грузить чек), но и опасна: её асинхронный ответ обновляет `transaction`
   * и может застать переход на `paymentUrl` ещё не завершённым (CPAYMENT-1846).
   */
  useEffect(() => {
    if (earlyIntentSentRef.current || !transaction) return;
    if (!REQUISITE_STATUSES.includes(transaction.status) || !isSupportedMethod || isRedirectMethod) return;
    // Короткое окно есть только у тикетов с обязательным чеком; остальным отметка ничего не
    // продлевает, зато переключила бы экран таймаута на «время истекло».
    if (transaction.simpleConfirmation !== false) return;
    if (transaction.receiptIntent) {
      earlyIntentSentRef.current = true;
      return;
    }

    earlyIntentSentRef.current = true;
    setShowConfirm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.status, transaction?.simpleConfirmation, transaction?.receiptIntent, isSupportedMethod, isRedirectMethod]);

  const handleConfirmWithReceipt = async () => {
    try {
      await onConfirm();
      setReceiptAccepted(true);
      setProcessingAfterUpload(true);
    } catch {
      setServerError(true);
    }
  };

  // One action: the receipt and the confirmation go in a single request; the screen that follows
  // is the same as after the old chain.
  const handleReceiptAndConfirm = async (file: File, handlers: ReceiptUploadHandlers) => {
    const result = await onConfirmWithReceipt(file, handlers);
    if (result.ok) {
      setReceiptAccepted(true);
      setProcessingAfterUpload(true);
    }
    return result;
  };

  // CPAYMENT-1616: re-upload in FILES_AUTO_VERIFICATION replaces the file server-side (no re-confirm —
  // the ticket is not in PAYMENT_PENDING). Show the wait screen until the poll returns the fresh phase.
  const handleReupload = async () => {
    setReuploading(true);
  };

  useEffect(() => {
    if (transaction?.receiptVerification?.phase === 'pending') {
      setReuploading(false);
    }
  }, [transaction?.receiptVerification?.phase]);

  /**
   * Leaving review takes the ticket through a requisite status for a moment: a resolved dispute or
   * a verified receipt is two commits, `FILES_VERIFICATION → PAYMENT_PENDING → terminal`. A poll
   * landing between them must not put the requisite back on screen, so a return from review is
   * believed only when the next read repeats it — and then the payment screen is the truth again.
   */
  const reviewSeenRef = useRef(false);
  const [regressReads, setRegressReads] = useState(0);
  useEffect(() => {
    if (!transaction) return;
    if (transaction.status === 'FILES_VERIFICATION' || transaction.status === 'FILES_AUTO_VERIFICATION') {
      reviewSeenRef.current = true;
      setRegressReads(0);
      return;
    }
    if (!reviewSeenRef.current) return;
    if (REQUISITE_STATUSES.includes(transaction.status)) {
      setRegressReads(prev => prev + 1);
    }
  }, [transaction]);

  const reviewRegressConfirmed = regressReads >= 2;
  useEffect(() => {
    if (!reviewRegressConfirmed) return;
    reviewSeenRef.current = false;
    setRegressReads(0);
    setProcessingAfterUpload(false);
    setReceiptAccepted(false);
  }, [reviewRegressConfirmed]);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return <GateWaitingScreen />;
  }

  // ─── Ожидание кончилось ничем (CPAYMENT-1716) ───────────────────────────────
  if (timeoutScreen && transaction) {
    return timeoutScreen === 'payment-expired' ? (
      <GatePaymentExpiredScreen
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onContactSupport={() => onRedirect(supportTarget(transaction))}
      />
    ) : (
      <GateReceiptUnconfirmedScreen
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onContactSupport={() => onRedirect(supportTarget(transaction))}
      />
    );
  }

  // ─── Error / no transaction ─────────────────────────────────────────────────
  if (error || !transaction) {
    return (
      <GateServerErrorScreen
        transaction={transaction!}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onReturn={() => transaction && onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Redirect-url paymentUrl in flight → bare spinner until navigation ──────
  if (
    transaction.requisite?.type === 'redirect_url' &&
    transaction.requisite?.paymentUrl
  ) {
    return <GateWaitingScreen title={t('gate.searchingTitle')} text={t('gate.searchingText')} />;
  }

  // ─── Выбор суммы депозита ───────────────────────────────────────────────────
  if (transaction.status === 'WAITING_FOR_VARIANT_SELECTION') {
    return (
      <GateVariantPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formatTime(timeLeft || 0)}
        onDeclineOffer={onDeclineOffer}
        onSelectVariant={onSelectVariant}
        variantSubmitting={variantSubmitting}
      />
    );
  }

  if (transaction.status === 'WAITING_FOR_REQUISITE') {
    return <GateWaitingScreen title={t('gate.searchingTitle')} text={t('gate.searchingText')} />;
  }

  // ─── CLIENT_PENDING — adapter not found ─────────────────────────────────────
  if (
    transaction.status === 'CLIENT_PENDING' &&
    !transaction.requisite?.type
  ) {
    if (!isSupportedMethod) {
      // Non-SBP/Card: bare spinner during retry phase; final state redirects via useEffect above.
      return <GateWaitingScreen title={t('gate.searchingTitle')} text={t('gate.searchingText')} />;
    }
    if ((transaction as any).paymentAdapterSearchAttemptsCounter > 0) {
      return (
        <GateRetryPage
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onRetry={() => setRetry(prev => prev + 1)}
          onBack={() => onRedirect(transaction.redirectUrl)}
        />
      );
    }
    return (
      <GateErrorScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onFinish={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── После оплаты: экран по этапу заявки, а не по сырому статусу ────────────
  if (processingAfterUpload && serverError) {
    return (
      <GateServerErrorScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onReturn={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  const stage = resolveGateStage(transaction, {
    confirmSent: processingAfterUpload,
    reuploading,
    reviewRegressHeld: reviewSeenRef.current && !reviewRegressConfirmed,
  });

  switch (stage) {
    case 'success':
      return (
        <GateSuccessPage
          transaction={transaction}
          uuid={uuid || ''}
          onRedirect={() => onRedirect(transaction.redirectUrl)}
        />
      );
    case 'receipt-unconfirmed':
      return (
        <GateReceiptUnconfirmedScreen
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onContactSupport={() => onRedirect(supportTarget(transaction))}
        />
      );
    case 'processing':
      return (
        <GatePaymentProcessingScreen
          uuid={uuid || ''}
          disputeOpen={transaction.disputeOpen}
          onFinish={() => onRedirect(transaction.redirectUrl)}
        />
      );
    // Failed check → let the client re-upload (no re-confirm). The stale "failed" stays hidden
    // while the just-uploaded file is being picked up by the next poll.
    case 'reupload':
      return (
        <GateUploadPage
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onConfirm={handleReupload}
          onBack={() => onRedirect(transaction.redirectUrl)}
          reupload
        />
      );
    case 'receipt-check':
      return (
        <GateProcessingScreen
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onBack={() => onRedirect(transaction.redirectUrl)}
          receiptAccepted={false}
        />
      );
    case 'declined':
      return (
        <GateErrorScreen
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onFinish={() => onRedirect(transaction.redirectUrl)}
          receiptAccepted={receiptAccepted}
        />
      );
    case 'expired':
      return (
        <GateExpiredScreen
          transaction={transaction}
          uuid={uuid || ''}
          onFinish={() => onRedirect(transaction.redirectUrl)}
        />
      );
    default:
      break;
  }

  // ─── Единственный экран оплаты: реквизиты + чек ─────────────────────────────
  if (REQUISITE_STATUSES.includes(transaction.status) && isSupportedMethod) {
    return (
      <GatePaymentPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onCancel={onCancel}
        onDeclineOffer={onDeclineOffer}
        cancelReasons={cancelReasons}
        onLeave={onLeave}
        receiptIntent={receiptIntent}
        onConfirm={handleConfirmWithReceipt}
        onConfirmWithReceipt={handleReceiptAndConfirm}
      />
    );
  }

  // Fallback spinner for unhandled states
  return <GateWaitingScreen />;
};

export default PayPageGateV1;
